import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn("Missing Supabase environment variables");
}
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ─── Types ────────────────────────────────────────────────

export type UserType = "casual" | "investor" | "landlord";

export type ProfileStatus =
  | "pending"
  | "in_progress"
  | "opted_out"
  | "newsletter_only"
  | "app_member"
  | "complete";

export type ExitStage =
  | "CAS1"
  | "CAS1_LANDLORD"
  | "CBS1"
  | "CAS2"
  | "CBS2"
  | "CAS3"
  | "CBS3"
  | "CAS4";

export interface Profile {
  id: string;
  name: string | null;
  email: string | null;
  user_type: UserType;
  token: string;
  session_complete: boolean;
  is_admin: boolean;
  status: ProfileStatus;
  exited_at: ExitStage | null;
  exit_detail: string | null;
  created_at: string;
}

// ─── Token lookup ─────────────────────────────────────────

export async function getProfileByToken(
  token: string
): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("leadgen_profiles")
    .select("*")
    .eq("token", token)
    .single();

  if (error) return null;
  return data;
}

// ─── Profile status updates ───────────────────────────────

export async function updateProfileStatus(
  profileId: string,
  status: ProfileStatus,
  exitedAt?: ExitStage,
  exitDetail?: string
): Promise<void> {
  const update: any = { status };
  if (exitedAt) update.exited_at = exitedAt;
  if (exitDetail) update.exit_detail = exitDetail;

  const { error } = await supabase
    .from("leadgen_profiles")
    .update(update)
    .eq("id", profileId);

  if (error) console.error("Profile status update failed:", error);
}

export async function markSessionComplete(profileId: string): Promise<void> {
  const { error } = await supabase
    .from("leadgen_profiles")
    .update({ session_complete: true })
    .eq("id", profileId);

  if (error) console.error("Failed to mark session complete:", error);
}

// ─── Lead gen data capture ────────────────────────────────

// Survey is saved WITHOUT profile_id — fully anonymised.
// anonymous_id is auto-generated by the DB (gen_random_uuid).
// Cannot be traced back to an individual even if you have their profile.
export async function saveSurveyResponse(data: {
  wellbeing_care: number;
  resonance: number;
  amenity_gap: number;
  gender: string;
  age_bracket: string;
  preferred_experience: string;
  concerns?: string;
  ideas?: string;
  qualified: boolean;
}): Promise<void> {
  const { error } = await supabase
    .from("leadgen_survey_responses")
    .insert(data);

  if (error) console.error("Survey save failed:", error);
}

export async function savePrebooking(
  profileId: string,
  data: {
    package_type: "single" | "five-pack";
    amount: number;
  }
): Promise<void> {
  const { error } = await supabase
    .from("leadgen_prebookings")
    .insert({ profile_id: profileId, ...data });

  if (error) console.error("Prebooking save failed:", error);
}

export async function saveInvestorExpression(
  profileId: string,
  data: {
    investment_tier: string;
    custom_amount?: number;
    accredited: boolean;
  }
): Promise<void> {
  const { error } = await supabase
    .from("leadgen_investor_expressions")
    .insert({ profile_id: profileId, ...data });

  if (error) console.error("Investor expression save failed:", error);
}

export async function saveNewsletterOptIn(
  profileId: string | null,
  splashType: string
): Promise<void> {
  const { error } = await supabase
    .from("leadgen_newsletter_optins")
    .insert({ profile_id: profileId, splash_type: splashType });

  if (error) console.error("Newsletter opt-in save failed:", error);
}

// ─── App Members ──────────────────────────────────────────

export async function saveAppMemberFromPrebooking(
  profile: Profile,
  packageType: "single" | "five-pack",
  amount: number
): Promise<void> {
  const { error } = await supabase.from("app_members").insert({
    leadgen_profile_id: profile.id,
    name: profile.name,
    email: profile.email,
    commitment_type: "prebooking",
    package_type: packageType,
    prebooking_amount: amount,
  });

  if (error) console.error("App member (prebooking) save failed:", error);
}

export async function saveAppMemberFromInvestor(
  profile: Profile,
  investmentTier: string,
  investmentAmount: number
): Promise<void> {
  const { error } = await supabase.from("app_members").insert({
    leadgen_profile_id: profile.id,
    name: profile.name,
    email: profile.email,
    commitment_type: "investor",
    investment_tier: investmentTier,
    investment_amount: investmentAmount,
  });

  if (error) console.error("App member (investor) save failed:", error);
}
